import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  InputAdornment,
  Grid,
} from "@mui/material";
import PersistentDataGrid from "../../components/table/PersistentDataGrid";
import { GridToolbar } from "@mui/x-data-grid";
import { usePaginationState } from "../../hooks/usePaginationState";
import ServicingApi from "../../services/AssociatedServices";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import SearchIcon from "@mui/icons-material/Search";
import jsPDF from "jspdf";
import translations from "../../assets/locals/translations";

const ServiveAndCustomerInfo = () => {
  const Services = new ServicingApi();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const [servicesData, setServicesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, handlePaginationModelChange] = usePaginationState('serviceAndCustomerInfoPaginationState', { pageSize: 50, page: 0 });

  const getCompleteCustomerInfo = async () => {
    setLoading(true);
    try {
      const response = await Services.getCompleteCustomerInfo();
      if (response && response.isSuccess) {
        setServicesData(response.result);
      }
    } catch (error) {
      console.error(error, "Error getting servicing list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCompleteCustomerInfo();
  }, []);

  const columns = [
    {
      field: "first_name",
      headerName: t.name || "Name",
      flex: 1,
      minWidth: 310,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => displayCompleteInfo(params.row)}
          underline="hover"
          sx={{ textTransform: "uppercase" }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: "model_name",
      headerName: t.modelName || "Model Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "engine_no",
      headerName: t.engineNumber || "Engine Number",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "chassis_no",
      headerName: t.chassisNumber || "Chassis Number",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "total_hours",
      headerName: t.totalHours || "Total Hours",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <span
          style={{
            textTransform: "uppercase",
            fontWeight: "bold",
            color: "#566573", // red
            fontFamily: "Roboto, sans-serif",
            fontSize: "0.9rem",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "delivery_date",
      headerName: t.deliveryDate || "Delivery Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const date = moment(params.row.delivery_date);
        return <span>{date.isValid() ? date.format("YYYY-MM-DD") : ""}</span>;
      },
    },
  ];
  const theme = useTheme();
  // Adjust grid container based on screen size for responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedCustomer, setSelectedCustomer] = useState({
    model: false,
    data: null,
  });

  // Handler to open the modal when engineNumber is clicked
  const displayCompleteInfo = (rowData) => {
    setSelectedCustomer({ model: true, data: rowData });
  };
  // Handler to close the modal
  const handleCloseModal = () => {
    setSelectedCustomer({ model: false, data: null });
  };

  const handleDownloadPdf = () => {
    // Select the element with your PDF content.
    const pdfContent = document.getElementById("pdf-content");
    if (!pdfContent) return;

    const doc = new jsPDF("p", "pt", "a4");

    // Use the html method to convert DOM to PDF
    doc.html(pdfContent, {
      callback: function (doc) {
        // Create a filename using the customer's first name and today's date
        const customerName = selectedCustomer?.data?.first_name || "Customer";
        const today = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD
        const filename = `${customerName}_${today}.pdf`;
        doc.save(filename);
      },
      margin: [20, 20, 20, 20],
      autoPaging: "text",
      x: 10,
      y: 10,
      html2canvas: { scale: 0.8 },
    });
  };

  const handlePageSizeChange = (newPageSize) => {
    handlePaginationModelChange({ ...paginationModel, pageSize: newPageSize });
  };
  // Global search state
  const [searchQuery, setSearchQuery] = useState("");

  // Filter rows based on search query across multiple fields
  const filteredRows = useMemo(() => {
    if (!searchQuery) return servicesData;
    return servicesData.filter((row) =>
      Object.keys(row).some((field) =>
        String(row[field]).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, servicesData]);

  return (
    <div className="bg-white px-2">
      {/* Header with title and global search input */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#333", // adjust to your desired color
            textAlign: "center", // centers the text
            letterSpacing: "0.5px", // adds spacing between letters
            textTransform: "uppercase", // optional: transforms text to uppercase
          }}
        >
          {t.detailedServiceOverview || "Detailed Service Overview"}
        </Typography>

        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "grey" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            marginLeft: "auto",
            width: isMobile ? "100%" : 300,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "lightgrey", // initial border color
              },
              "&.Mui-focused": {
                boxShadow: "none", // remove focus shadow
                "& fieldset": {
                  borderColor: "lightgrey", // keep border color the same on focus
                },
              },
            },
          }}
        />
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
      >
        <PersistentDataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.id}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 15, 25, 50]}
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          disableSelectionOnClick
          autoHeight={isMobile}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>{t.noDataAvailable || "No Data Available"}</span>
              </div>
            ),
          }}
          componentsProps={{
            toolbar: {
              position: "right",
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{ width: "100%" }}
          loading={loading}
        />
      </div>

      {/* Vehicle & Customer Complete Info Detail Modal */}
      <Dialog
        open={selectedCustomer.model}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            p: 2,
            fontFamily: "Roboto, sans-serif",
          },
        }}
      >
        {selectedCustomer.model && (
          <>
            <DialogTitle
              sx={{
                fontWeight: 700,
                fontSize: "1.125rem",
                borderBottom: "1px solid #e0e0e0",
                padding: 0,
                mb: 2,
                textAlign: "center",
              }}
            >
              Vehicle &amp; Customer Info
            </DialogTitle>

            <DialogContent
              sx={{ backgroundColor: "#fafafa", px: 2, py: 1 }}
              id="pdf-content"
            >
              {/* Section: Personal Information */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Personal Information
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>First Name:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.personal?.first_name || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>WhatsApp Number:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.personal?.whatsapp_number ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Mobile Number:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.personal?.phone_number || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Email:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.personal?.email || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Area Detail */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Area Detail
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>District:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.area?.district || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Taluka:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.area?.taluka || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Village:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.area?.village || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Sales Person:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.area?.sales_person || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: New Product Detail */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  New Product Detail
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Manufacture Name:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.new?.manufacturer || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Model Name:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.new?.model_name || "-"}
                      </span>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Engine Number:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.new?.engine_no || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Chassis Number:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.new?.chassis_no || "-"}
                      </span>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Delivery Date:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {moment(selectedCustomer?.data?.delivery_date).format(
                          "YYYY-MM-DD"
                        ) || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>New Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.new?.new_tractor_price || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Remaining Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.new?.remaining_price || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Old Product Detail */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Old Product Detail
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Old Tractor Owned:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.old?.isOldOwned || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Old Make:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.old?.oldmake || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Old Model:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.old?.oldmodel || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Condition:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.old?.old_condition || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Old Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.old?.old_tractor_price || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Total Hours:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.total_hours || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Bank Detail */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Bank Detail
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Payment Method:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.payment?.payment_mode || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Payment Details:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.payment?.payment_details ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Remaining Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.payment?.remaining_price ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Enquiry Detail */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Enquiry Detail
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Primary Source:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.source?.primary_source || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Enquiry Source:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.source?.enquiry_source || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Enquiry Date:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.source?.enquiry_date || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Enquiry Stage:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.source?.enquiry_stage || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Delivery Details */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Delivery Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Manufacture:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_manufacture_name ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Model Name:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_model_name || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Engine No:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_engine_no || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Chassis No:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_chassis_no || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>New Tractor Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery
                          ?.d_new_tractor_price || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Old Tractor Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery
                          ?.d_old_tractor_price || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Remaining Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_remaining_price ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Payment Mode:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_payment_mode ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Payment Details:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_payment_details ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Bank Name:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_bankname || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Delivery Date:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_delivery_date ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Salesman:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.delivery_salesman ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Created At:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.delivery?.d_dcreated_at || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Invalid Details */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Invalid Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Invalid Reason:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.invalid?.invalid_reason || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Invalid By:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.invalid?.invalid_by || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Invalid Date:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.invalid?.invalid_date || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Lost Details */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Lost Details
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>New Tractor Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.lost?.l_new_tractor_price ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Old Tractor Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.lost?.l_old_tractor_price ||
                          "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Remaining Price:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.lost?.l_remaining_price || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Lost By:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.lost?.lost_by || "-"}
                      </span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: "#555" }}>
                      <strong>Lost Date:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {selectedCustomer?.data?.lost?.lost_date || "-"}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Section: Follow Up Details */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 1, color: "#333" }}
                >
                  Remarks Details
                </Typography>
                {selectedCustomer?.data?.Follow_up?.length > 0 ? (
                  selectedCustomer.data.Follow_up.map((followup, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{ color: "#555", mb: 0.5 }}
                    >
                      {followup.followup_date} {" "}
                      <span style={{ color: "#000" }}>
                        {followup.discussion}
                      </span>
                      <strong>Followed By:</strong>{" "}
                      <span style={{ color: "#000" }}>
                        {followup.followed_by || "-"}
                      </span>
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: "#555" }}>
                    No follow-up records found.
                  </Typography>
                )}
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: "center",
                pt: 1,
                // padding: 0,
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <Button
                variant="text"
                color="primary"
                sx={{
                  textTransform: "none",
                  mr: 1,
                  backgroundColor: "#e0f7fa", // Light cyan background
                  border: "1px solid #00acc1", // Vibrant cyan border
                  borderRadius: 1,
                  transition:
                    "background-color 0.3s ease, border-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#b2ebf2", // Slightly darker on hover
                    borderColor: "#00838f",
                  },
                }}
                onClick={handleDownloadPdf}
              >
                {t.downloadPdf || "Download PDF"}
              </Button>
              <Button
                variant="text"
                color="primary"
                sx={{
                  textTransform: "none",
                  backgroundColor: "#fff3e0", // Light orange background
                  border: "1px solid #ffb74d", // Vibrant orange border
                  borderRadius: 1,
                  transition:
                    "background-color 0.3s ease, border-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#ffe0b2", // Lighter orange on hover
                    borderColor: "#fb8c00",
                  },
                }}
                onClick={handleCloseModal}
              >
                {t.back || "Back"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ServiveAndCustomerInfo;
